import { useRef } from "react";

import { type ErrorType } from "@vega-api/configs";
import {
  useUploadCoinvestNdaDocument,
  useUploadCoinvestNdaWordDocumentV2,
} from "@vega-api/generated/coinvest-ndas-upload/coinvest-ndas-upload";
import { useGetDealGeneralPartnerViewV3 } from "@vega-api/generated/deal/deal";
import { useToast } from "@vega-core/components/Toast/useToast";
import { m } from "@vega-core/paraglide/messages";
import { useCoInvestErrorToast } from "@vega-modules/hooks/useCoInvestErrorToast";

export const useNdaUpload = (dealId: string) => {
  const showToast = useToast();
  const showErrorToast = useCoInvestErrorToast();

  const {
    data: dealGPViewResponse,
    refetch: refetchGPView,
    isPending: isPendingGPView,
  } = useGetDealGeneralPartnerViewV3(dealId, {
    query: {
      enabled: !!dealId,
    },
  });

  const pdfInputRef = useRef<HTMLInputElement>(null);
  const wordInputRef = useRef<HTMLInputElement>(null);

  const handleUploadSuccess = (
    inputRef: React.RefObject<HTMLInputElement | null>,
  ) => {
    showToast({
      variant: "success",
      title: m.status_success(),
      description: m.coInvestments_ndaUploadSuccess(),
    });
    refetchGPView();

    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const handleUploadError = (error: ErrorType<unknown>) => {
    showErrorToast(error, {
      defaultMessage: m.coInvestments_error_uploadNda(),
    });
  };

  const uploadPdfMutation = useUploadCoinvestNdaDocument({
    mutation: {
      onSuccess: () => handleUploadSuccess(pdfInputRef),
      onError: handleUploadError,
    },
  });

  const uploadWordMutation = useUploadCoinvestNdaWordDocumentV2({
    mutation: {
      onSuccess: () => handleUploadSuccess(wordInputRef),
      onError: handleUploadError,
    },
  });

  const createFileSelectHandler =
    (uploadMutation: {
      mutate: (variables: { data: { dealId: string; file: File } }) => void;
    }) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];

      if (file && dealId) {
        uploadMutation.mutate({
          data: { dealId, file },
        });
      }
    };

  const handlePdfFileSelect = createFileSelectHandler(uploadPdfMutation);
  const handleWordFileSelect = createFileSelectHandler(uploadWordMutation);

  return {
    dealGPViewResponse,
    refetchGPView,
    isPendingGPView,
    uploadPdfMutation,
    uploadWordMutation,
    pdfInputRef,
    wordInputRef,
    handlePdfFileSelect,
    handleWordFileSelect,
  };
};
